/**
 * CanvasUrlSync
 *
 * Bidirectional sync between the active canvas and the URL hash so that
 * browser back/forward steps through canvas selections instead of leaving
 * the page. Lives inside `CanvasProvider` on pages that opt in (Operator,
 * Builder).
 *
 * Hash shape: `#c=<n>` where n is the 1-based position in the sorted
 * canvas list (default canvas = 1, mirroring number-row keyboard
 * shortcuts so 1→leftmost tab, 2→next, etc.). When the default canvas is
 * active the hash is omitted entirely so the common URL stays clean.
 *
 * A single effect drives both directions, deciding which side won by
 * comparing this render's deps to the previous render via refs:
 *  - hash changed → URL is authoritative (popstate, deep link, our own push)
 *  - sorted-list grew → re-attempt URL→state (deep link arrived before
 *    canvases finished loading)
 *  - active index changed → state is authoritative (user click, reorder)
 * Two independent effects would oscillate on a tab click because both would
 * observe the post-state-change render with a stale URL and try to fix it
 * in opposite directions.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCanvases } from '../../contexts/CanvasContext';
import { compareCanvases } from '../../types/canvases';

const HASH_KEY = 'c';

const parseHash = (h: string) => new URLSearchParams(h.replace(/^#/, ''));

// The hash is 1-based so it maps cleanly onto number-row keyboard shortcuts
// (1=default, 2=next, …). Internally we keep `sorted` 0-based for array
// access, so these helpers convert at the boundary.
const readIndex = (h: string): number => {
  const raw = parseHash(h).get(HASH_KEY);
  if (!raw) return 0;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return 0;
  return n - 1;
};

const writeIndex = (h: string, idx: number): string => {
  const p = parseHash(h);
  if (idx <= 0) p.delete(HASH_KEY);
  else p.set(HASH_KEY, String(idx + 1));
  const s = p.toString();
  return s ? `#${s}` : '';
};

export function CanvasUrlSync() {
  const { canvases, activeCanvasId, setActiveCanvasId } = useCanvases();
  const location = useLocation();
  const navigate = useNavigate();

  const sorted = useMemo(
    () => [...canvases.values()].sort(compareCanvases),
    [canvases],
  );

  const urlIndex = readIndex(location.hash);
  const activeIndex = activeCanvasId
    ? sorted.findIndex((w) => w.canvasId === activeCanvasId)
    : -1;

  const prevHashRef = useRef<string | null>(null);
  const prevActiveIndexRef = useRef<number | null>(null);
  const prevSortedLenRef = useRef(0);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const hashChanged = prevHashRef.current !== location.hash;
    const activeIndexChanged = prevActiveIndexRef.current !== activeIndex;
    const sortedGrew = prevSortedLenRef.current < sorted.length;

    prevHashRef.current = location.hash;
    prevActiveIndexRef.current = activeIndex;
    prevSortedLenRef.current = sorted.length;

    // URL → state: hash moved (popstate / deep link / our own push), or the
    // canvas list just grew enough to resolve a URL that was previously
    // out of range.
    if (hashChanged || sortedGrew) {
      const target = sorted[urlIndex];
      if (target) {
        if (target.canvasId !== activeCanvasId) {
          setActiveCanvasId(target.canvasId);
        }
        hasInitializedRef.current = true;
        return;
      }
      // urlIndex still out of range — fall through so State → URL cleans
      // up the hash.
    }

    // State → URL: active canvas's index changed (user click, reorder,
    // programmatic switch), or this is the first run with an unresolved URL.
    if (activeIndexChanged || !hasInitializedRef.current) {
      if (activeIndex < 0) return;
      if (activeIndex === urlIndex) {
        hasInitializedRef.current = true;
        return;
      }
      const nextHash = writeIndex(location.hash, activeIndex);
      navigate(
        { pathname: location.pathname, search: location.search, hash: nextHash },
        { replace: !hasInitializedRef.current },
      );
      hasInitializedRef.current = true;
    }
  }, [
    location.hash,
    location.pathname,
    location.search,
    urlIndex,
    activeIndex,
    activeCanvasId,
    sorted,
    setActiveCanvasId,
    navigate,
  ]);

  return null;
}
